'use client';

import { ComponentType, useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { SectionWrapper } from '@/components/ui/section-wrapper';

interface MLMPlan {
    slug: string;
    name: string;
    description: string;
    content: string;
    image?: string;
    features?: string[];
}

interface PlanDetailLoaderProps {
    plan: MLMPlan;
    englishSlug?: string; // English slug for matching the component
}

// Import plan-specific components
// Using require() with try-catch to handle missing components gracefully
let BinaryPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let MatrixPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let MMMGlobalPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let AustralianBinaryPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let EmgoldexPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let HelpPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let GenerationPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let ClickPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let RepurchasePlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let ForcedMatrixPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let MonolinePlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let SpilloverBinaryPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let GiftPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let PartyPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let InvestmentPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let CrowdFundingPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let XUpPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let AutofillPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let StairStepPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let HybridPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let BoardPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;
let UnilevelPlanContent: ComponentType<{ plan: MLMPlan }> | null = null;

// Load components with error handling
try {
    BinaryPlanContent = require('./binary-plan/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    MatrixPlanContent = require('./matrix-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    MMMGlobalPlanContent = require('./mmm-global-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    AustralianBinaryPlanContent = require('./australian-binary-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    EmgoldexPlanContent = require('./emgoldex-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    HelpPlanContent = require('./help-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    GenerationPlanContent = require('./generation-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    ClickPlanContent = require('./click-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    RepurchasePlanContent = require('./repurchase-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    ForcedMatrixPlanContent = require('./forced-matrix-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    MonolinePlanContent = require('./monoline-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    SpilloverBinaryPlanContent = require('./spillover-binary-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    GiftPlanContent = require('./gift-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    PartyPlanContent = require('./party-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    InvestmentPlanContent = require('./investment-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    CrowdFundingPlanContent = require('./crowd-funding-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    XUpPlanContent = require('./x-up-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    AutofillPlanContent = require('./autofill-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    StairStepPlanContent = require('./stair-step-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    HybridPlanContent = require('./hybrid-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    BoardPlanContent = require('./board-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

try {
    UnilevelPlanContent = require('./unilevel-plan-mlm-software/PlanDetailContent').default;
} catch (e) {
    // Component not found or failed to load
}

// Map plan slugs to their specific components
const planComponentMap: Record<string, ComponentType<{ plan: MLMPlan }> | null> = {
    // Binary Plan
    'binary-mlm-plan': BinaryPlanContent,
    'binary-mlm-software': BinaryPlanContent,
    'plan-binario': BinaryPlanContent,
    'plan-binaire': BinaryPlanContent,
    'piano-binario': BinaryPlanContent,
    'binaere-plaene': BinaryPlanContent,
    // Matrix Plan
    'matrix-plan': MatrixPlanContent,
    'matrix-plan-mlm-software': MatrixPlanContent,
    'matrix-mlm-software': MatrixPlanContent,
    'plan-matriz': MatrixPlanContent,
    'plan-matriciel': MatrixPlanContent,
    // Unilevel Plan
    'unilevel-plan-mlm-software': UnilevelPlanContent,
    'unilevel-plan': UnilevelPlanContent,
    'unilevel': UnilevelPlanContent,
    'plan-uninivel': UnilevelPlanContent,
    'plan-uniniveau': UnilevelPlanContent,
    'piano-unilevel': UnilevelPlanContent,
    // Board Plan
    'board-plan-mlm-software': BoardPlanContent,
    'board-plan': BoardPlanContent,
    'plan-de-tablero': BoardPlanContent,
    'plan-de-conseil': BoardPlanContent,
    'piano-del-consiglio': BoardPlanContent,
    'vorstandsplan': BoardPlanContent,
    // Hybrid Plan
    'hybrid-plan-mlm-software': HybridPlanContent,
    'hybrid-plan': HybridPlanContent,
    'planes-hibridos': HybridPlanContent,
    'plan-hybride': HybridPlanContent,
    'piano-ibrido': HybridPlanContent,
    'fuer-hybridplaene': HybridPlanContent,
    // Stair Step Plan
    'stair-step-plan-mlm-software': StairStepPlanContent,
    'stair-step-plan': StairStepPlanContent,
    'planes-de-escalones': StairStepPlanContent,
    'plan-descalier': StairStepPlanContent,
    'piano-delle-scale': StairStepPlanContent,
    'treppenstufenplan': StairStepPlanContent,
    // Autofill Plan
    'autofill-plan-mlm-software': AutofillPlanContent,
    'autofill-plan': AutofillPlanContent,
    'software-mlm-de-plan-de-autocompletar': AutofillPlanContent,
    'logiciel-mlm-de-plan-de-remplissage-automatique': AutofillPlanContent,
    'software-mlm-del-piano-di-riempimento-automatico': AutofillPlanContent,
    'plan-mlm-software-zum-automatischen-ausfuellen': AutofillPlanContent,
    // X-Up Plan
    'x-up-plan-mlm-software': XUpPlanContent,
    'x-up-plan': XUpPlanContent,
    'software-mlm-plan-x-up': XUpPlanContent,
    'software-x-up-plan-mlm': XUpPlanContent,
    'software-mlm-del-piano-x-up': XUpPlanContent,
    // Crowd Funding Plan
    'crowd-funding-plan-mlm-software': CrowdFundingPlanContent,
    'crowd-funding-plan': CrowdFundingPlanContent,
    // Investment Plan
    'investment-plan-mlm-software': InvestmentPlanContent,
    'investment-plan': InvestmentPlanContent,
    'software-de-planes-de-inversion-mlm': InvestmentPlanContent,
    'plan-dinvestissement-logiciel-mlm': InvestmentPlanContent,
    'software-per-piani-di-investimento-mlm': InvestmentPlanContent,
    'investitionsplan-mlm-software': InvestmentPlanContent,
    // Party Plan
    'party-plan-mlm-software': PartyPlanContent,
    'party-plan': PartyPlanContent,
    'software-mlm-para-planificar-fiestas': PartyPlanContent,
    'logiciel-mlm-plan-de-fete': PartyPlanContent,
    'software-mlm-per-piano-feste': PartyPlanContent,
    // Australian Binary Plan
    'australian-binary-plan-mlm-software': AustralianBinaryPlanContent,
    'australian-binary-plan': AustralianBinaryPlanContent,
    'software-mlm-de-plan-binario-australiano': AustralianBinaryPlanContent,
    'logiciel-australien-de-plan-binaire-mlm': AustralianBinaryPlanContent,
    'software-mlm-per-piani-binari-australiani': AustralianBinaryPlanContent,
    'mlm-software-fuer-den-australischen-binaerplan': AustralianBinaryPlanContent,
    // MMM Global Plan
    'mmm-global-plan-mlm-software': MMMGlobalPlanContent,
    'mmm-global': MMMGlobalPlanContent,
    'mmm-plan-global-software-mlm': MMMGlobalPlanContent,
    'logiciel-mlm-global-plan-mlm': MMMGlobalPlanContent,
    'mmm-piano-globale-software-mlm': MMMGlobalPlanContent,
    // Emgoldex Plan
    'emgoldex-plan-mlm-software': EmgoldexPlanContent,
    'emgoldex-plan': EmgoldexPlanContent,
    'logiciel-emgoldex-plan-mlm': EmgoldexPlanContent,
    'software-mlm-piano-emgoldex': EmgoldexPlanContent,
    // Forced Matrix Plan
    'forced-matrix-plan-mlm-software': ForcedMatrixPlanContent,
    'forced-matrix-plan': ForcedMatrixPlanContent,
    'software-mlm-plan-de-matriz-forzada': ForcedMatrixPlanContent,
    'logiciel-mlm-a-plan-matriciel-force': ForcedMatrixPlanContent,
    'software-mlm-con-piano-a-matrice-forzata': ForcedMatrixPlanContent,
    // Help Plan
    'help-plan-mlm-software': HelpPlanContent,
    'help-plan': HelpPlanContent,
    'ayuda-a-planificar-el-software-mlm': HelpPlanContent,
    'aide-planifier-un-logiciel-mlm': HelpPlanContent,
    'aiuta-a-pianificare-il-software-mlm': HelpPlanContent,
    'hilfe-beim-planen-von-mlm-software': HelpPlanContent,
    // Repurchase Plan
    'repurchase-plan-mlm-software': RepurchasePlanContent,
    'repurchase-plan': RepurchasePlanContent,
    'plan-de-recompra-de-software-mlm': RepurchasePlanContent,
    'logiciel-mlm-de-plan-de-rachat': RepurchasePlanContent,
    'piano-di-riacquisto-del-software-mlm': RepurchasePlanContent,
    'rueckkaufplan-mlm-software': RepurchasePlanContent,
    // Click Plan
    'click-plan-mlm-software': ClickPlanContent,
    'click-plan': ClickPlanContent,
    'haga-clic-en-el-software-de-plan-mlm': ClickPlanContent,
    'cliquez-sur-plan-logiciel-mlm': ClickPlanContent,
    'fare-clic-sul-software-del-piano-mlm': ClickPlanContent,
    'klicken-sie-auf-mlm-software-planen': ClickPlanContent,
    // Generation Plan
    'generation-plan-mlm-software': GenerationPlanContent,
    'generation-plan': GenerationPlanContent,
    'software-de-generacion-de-planes-mlm': GenerationPlanContent,
    'logiciel-mlm-de-plan-de-generation': GenerationPlanContent,
    'software-mlm-piano-di-generazione': GenerationPlanContent,
    // Monoline Plan
    'monoline-plan-mlm-software': MonolinePlanContent,
    'monoline-plan': MonolinePlanContent,
    'software-mlm-plan-monolinea': MonolinePlanContent,
    'logiciel-mlm-de-plan-monoline': MonolinePlanContent,
    'software-mlm-piano-monolinea': MonolinePlanContent,
    // Spillover Binary Plan
    'spillover-binary-plan-mlm-software': SpilloverBinaryPlanContent,
    'spillover-binary-plan': SpilloverBinaryPlanContent,
    'software-mlm-de-plan-binario-de-desbordamiento': SpilloverBinaryPlanContent,
    'logiciel-mlm-de-plan-binaire-de-debordement': SpilloverBinaryPlanContent,
    'logiciel-mlm-de-plan-debordement-binaire': SpilloverBinaryPlanContent,
    'software-mlm-per-piani-binari-di-spillover': SpilloverBinaryPlanContent,
    'spillover-binaerplan-mlm-software': SpilloverBinaryPlanContent,
    // Gift Plan
    'gift-plan-mlm-software': GiftPlanContent,
    'gift-plan': GiftPlanContent,
};

// Minimal fallback component (for plans without custom folders)
function DefaultPlanContent({ plan }: { plan: MLMPlan }) {
    // Sanitize HTML content to prevent XSS attacks
    const sanitizedContent = useMemo(() => {
        if (!plan.content) return null;
        return DOMPurify.sanitize(plan.content, {
            ALLOWED_TAGS: [
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'p', 'br', 'hr',
                'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'small', 'sub', 'sup',
                'ul', 'ol', 'li',
                'blockquote', 'pre', 'code',
                'a', 'img', 'figure', 'figcaption',
                'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
                'div', 'span', 'section', 'article',
            ],
            ALLOWED_ATTR: [
                'href', 'target', 'rel', 'title',
                'src', 'alt', 'width', 'height', 'loading', 'referrerpolicy',
                'class', 'id',
                'colspan', 'rowspan',
            ],
            ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
            FORBID_ATTR: ['onerror', 'onload', 'oninput', 'onclick', 'onmouseover'],
            FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style', 'form', 'input', 'button'],
            ALLOW_DATA_ATTR: false,
        });
    }, [plan.content]);

    return (
        <div className="min-h-screen bg-white dark:bg-gray-950">
            <SectionWrapper variant="default" padding="lg" className="relative">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        {plan.name}
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                        {plan.description}
                    </p>
                    {sanitizedContent && (
                        <div
                            className="prose prose-lg dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                        />
                    )}
                </div>
            </SectionWrapper>
        </div>
    );
}

export function PlanDetailLoader({ plan, englishSlug }: PlanDetailLoaderProps) {
    // Normalize slug format: convert mlm-forced-matrix-plan to forced-matrix-plan-mlm-software
    let normalizedSlug = plan.slug;
    if (plan.slug === 'mlm-forced-matrix-plan') {
        normalizedSlug = 'forced-matrix-plan-mlm-software';
    }

    // Try to find plan-specific component by slug first (for English pages)
    let PlanSpecificContent = planComponentMap[normalizedSlug];

    // If not found and we have an English slug, try matching by English slug
    // This is the most reliable way to match translated plans to their components
    if (!PlanSpecificContent && englishSlug) {
        const normalizedEnglishSlug = englishSlug === 'mlm-forced-matrix-plan'
            ? 'forced-matrix-plan-mlm-software'
            : englishSlug;
        PlanSpecificContent = planComponentMap[normalizedEnglishSlug];
    }

    // If still not found, try to match by finding the English equivalent slug using keyword matching
    // This handles translated slugs as a fallback (e.g., 'plan-mondial-mmm' -> 'mmm-global-plan-mlm-software')
    if (!PlanSpecificContent && plan.image) {
        // Try to find matching English slug by checking common patterns
        // For MMM Global Plan translations
        if (normalizedSlug.includes('mondial') || normalizedSlug.includes('mundial') || normalizedSlug.includes('global') || normalizedSlug.includes('mmm') || normalizedSlug.includes('globale')) {
            PlanSpecificContent = MMMGlobalPlanContent;
        }
        // For Binary Plan translations
        else if (normalizedSlug.includes('binaire') || normalizedSlug.includes('binar') || normalizedSlug.includes('binary')) {
            PlanSpecificContent = BinaryPlanContent;
        }
        // For Matrix Plan translations
        else if (normalizedSlug.includes('matrice') || normalizedSlug.includes('matrix') || normalizedSlug.includes('matriciel')) {
            PlanSpecificContent = MatrixPlanContent;
        }
        // For Unilevel Plan translations
        else if (normalizedSlug.includes('uniniveau') || normalizedSlug.includes('uninivel') || normalizedSlug.includes('unilevel')) {
            PlanSpecificContent = UnilevelPlanContent;
        }
        // For Board Plan translations
        else if (normalizedSlug.includes('tableau') || normalizedSlug.includes('board') || normalizedSlug.includes('vorstand')) {
            PlanSpecificContent = BoardPlanContent;
        }
        // For Generation Plan translations
        else if (normalizedSlug.includes('generation') || normalizedSlug.includes('generacion') || normalizedSlug.includes('génération')) {
            PlanSpecificContent = GenerationPlanContent;
        }
        // For Gift Plan translations
        else if (normalizedSlug.includes('regalo') || normalizedSlug.includes('cadeau') || normalizedSlug.includes('gift') || normalizedSlug.includes('geschenk')) {
            PlanSpecificContent = GiftPlanContent;
        }
        // For Help Plan translations
        else if (normalizedSlug.includes('aide') || normalizedSlug.includes('ayuda') || normalizedSlug.includes('help') || normalizedSlug.includes('hilfe')) {
            PlanSpecificContent = HelpPlanContent;
        }
        // For Australian Binary Plan translations
        else if (normalizedSlug.includes('australian') || normalizedSlug.includes('australien') || normalizedSlug.includes('australiano')) {
            PlanSpecificContent = AustralianBinaryPlanContent;
        }
        // For Emgoldex Plan translations
        else if (normalizedSlug.includes('emgoldex')) {
            PlanSpecificContent = EmgoldexPlanContent;
        }
        // For Autofill Plan translations
        else if (normalizedSlug.includes('autofill') || normalizedSlug.includes('auto-fill') || normalizedSlug.includes('autocompletar')) {
            PlanSpecificContent = AutofillPlanContent;
        }
    }

    // If still not found, try partial slug matching
    if (!PlanSpecificContent) {
        const slugToMatch = englishSlug || normalizedSlug;
        const slugParts = slugToMatch.split('-');
        for (let i = slugParts.length; i > 0; i--) {
            const partialSlug = slugParts.slice(0, i).join('-');
            if (planComponentMap[partialSlug]) {
                PlanSpecificContent = planComponentMap[partialSlug];
                break;
            }
        }
    }

    // Use plan-specific component if available, otherwise use minimal fallback
    // Check if component is actually a valid React component before rendering
    if (PlanSpecificContent && typeof PlanSpecificContent === 'function') {
        try {
            return <PlanSpecificContent plan={plan} />;
        } catch (error) {
            // If component fails to render, fall back to default content
            console.error('Error rendering plan component:', error);
            return <DefaultPlanContent plan={plan} />;
        }
    }
    
    // Fallback to default content if no specific component found
    return <DefaultPlanContent plan={plan} />;
}
